<template>
  <v-app-bar
    :clipped-left="screenSize.mdAndUp"
    :clipped-right="screenSize.mdAndUp"
    app
    :color="toolbarColor"
    dark
    extension-height="60px"
  >
    <!-- Menu button appears when in mobile view to provide access to menu -->
    <v-app-bar-nav-icon
      v-if="!onlyShowProfileAvatar"
      data-qa="toolbar_hamburger"
      @click="toggleMenu"
    />

    <v-toolbar-title class="bm-toolbar-title">
      <v-btn text @click="navigateHome">
        <img
          height="24"
          src="@/assets/images/brewman_white.png"
          alt="BrewMan logo"
        />
      </v-btn>
    </v-toolbar-title>

    <template v-if="!onlyShowProfileAvatar">
      <bm-search-box v-if="screenSize.smAndUp" />
      <v-btn
        v-else
        icon
        :style="showSearchBarMobile ? { color: '#ffb010' } : {}"
        @click="showSearchBarMobile = !showSearchBarMobile"
      >
        <bm-icon icon="Search" />
      </v-btn>

      <v-spacer />
      <v-btn
        v-if="screenSize.smAndUp && isFullscreenSupported()"
        icon
        @click="toggleFullscreen"
      >
        <bm-icon :icon="isFullscreen ? 'FullscreenExit' : 'Fullscreen'" />
      </v-btn>
      <bm-add-activity />
    </template>

    <template
      v-if="screenSize.xs && showSearchBarMobile && !onlyShowProfileAvatar"
      #extension
    >
      <bm-search-box />
    </template>

    <v-btn
      v-if="helpLink && !onlyShowProfileAvatar"
      :href="helpLink"
      icon
      target="_blank"
    >
      <bm-icon icon="MenuBook" />
    </v-btn>

    <v-menu v-if="!onlyShowProfileAvatar && screenSize.mdAndUp" offset-y>
      <template #activator="{ on }">
        <v-btn icon v-on="on">
          <v-icon
            size="20px"
            :class="localeToFlagCss(currentLanguageLocale)"
          ></v-icon>
        </v-btn>
      </template>

      <v-list>
        <v-list-item-group :value="currentLanguageLocale.langLocale">
          <v-list-item
            v-for="langLocaleDefinition in availableLanguageLocales"
            :key="langLocaleDefinition.langLocale"
            :value="langLocaleDefinition.langLocale"
            @click="setLanguage(langLocaleDefinition.langLocale)"
          >
            <v-list-item-title>
              <v-icon
                size="20px"
                :class="localeToFlagCss(langLocaleDefinition)"
                style="height: 20px"
              ></v-icon>
              {{ langLocaleDefinition.nativeDescription }}
            </v-list-item-title>
          </v-list-item>
        </v-list-item-group>
      </v-list>
    </v-menu>
    <bm-btn
      v-if="!onlyShowProfileAvatar && screenSize.smAndDown"
      icon
      button-type="ghost"
      :to="{ name: 'distributionapp_redirect' }"
    >
      <bm-icon icon="Van" />
    </bm-btn>
    <v-avatar
      v-if="!onlyShowProfileAvatar && tenantSettings.showTenantIndicator"
      :color="tenantSettings.tenantIndicatorColour"
      size="40"
    >
      <span class="white--text">{{ tenantSettings.tenantIndicatorText }}</span>
    </v-avatar>

    <v-spacer v-if="onlyShowProfileAvatar" />
    <bm-profile-avatar />
  </v-app-bar>
</template>

<script setup lang="ts">
import BmBtn from "@/components/controls/input/BmBtn.vue";
import BmProfileAvatar from "@/components/controls/app/BmProfileAvatar.vue";
import BmAddActivity from "@/components/controls/app/BmAddActivity.vue";
import userStore from "@/store/modules/userStore";
import appStore from "@/store/modules/appStore";
import BmSearchBox from "@/components/controls/search/BmSearchBox.vue";
import env from "@/environment";
import { computed, ref } from "vue";

import { useRoute } from "vue-router/composables";
import router from "@/router/router";
import { useFullscreen } from "@/components/composables/clientUtils";
import { useLocaleSelection } from "@/locales/useLocaleSelection";
import { customColoursLight } from "@/plugins/vuetify";
import tenantStore from "@/store/modules/tenantStore";
import { mainRoutes } from "@/app/ui/navigation/mainMenu";
import { screenSize } from "@/vue23switching/screenSize";

interface BmToolbarProps {
  onlyShowProfileAvatar: boolean;
}

const props = defineProps<BmToolbarProps>();

interface BmToolbarEvents {
  (event: "toggle-menu"): void;
}

const emit = defineEmits<BmToolbarEvents>();

const showSearchBarMobile = ref(false);

const route = useRoute();

const {
  availableLanguageLocales,
  currentLanguageLocale,
  localeToFlagCss,
  setLanguage,
} = useLocaleSelection();

const helpLink = computed(() => {
  return appStore.state.contextualHelpLink;
});
const toolbarColor = computed(() => {
  const isPrimary = userStore.state.tenant?.isPrimary ?? true;

  if (env.isDevelopment) {
    return isPrimary
      ? customColoursLight.brand04Hover
      : customColoursLight.successMedium;
  }

  return isPrimary ? customColoursLight.brand01 : customColoursLight.brand02;
});

const toggleMenu = () => {
  emit("toggle-menu");
};
const navigateHome = () => {
  if (route.name !== mainRoutes.home.name && !props.onlyShowProfileAvatar) {
    router.push(mainRoutes.home.location());
  }
};

const tenantSettings = computed(() => tenantStore.state.tenantSettings);

const { isFullscreenSupported, toggleFullscreen, isFullscreen } =
  useFullscreen();
</script>

<style scoped lang="sass">
.bm-toolbar-title
  width: 300px
  margin-left: 0 !important
  padding-left: 0 !important
</style>
